// A BEVÉTEL-SZŰRŐK — a Pénzügy lap KÉT fülének KÖZÖS szűrő-készlete (Összegzés + Bevételek).
//
// MIÉRT KÖZÖS HOOK: a szűrők ugyanazt a halmazt szűkítik, csak az egyik fül AGGREGÁTUMOT (kártyák, chartok),
// a másik LISTÁT rajzol belőle. Ha a két fülnek külön szűrő-implementációja lenne, ugyanaz a beállítás
// előbb-utóbb mást jelentene a két nézetben — és a felhasználó a fül-váltáskor „elvesztené" a szűrését.
// A szűrő-állapot ezért EGY URL-kulcs-készleten (`rev_` prefix) él: a fül-váltás megtartja a szűrést.
//
// A KASZKÁD-RESET (a legacy 1:1): fő→(al+szolgáltatás+csomag) · al→(szolgáltatás+csomag) · szolgáltatás→csomag ·
// ügyfél→fiók. Mindegyik EGYETLEN `setFilters` hívás: ami atomi szándék, az egy navigáció legyen.
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ALL_FILTER_VALUE as ALL, type ToolbarFilterItem } from '@/components/shared/list-toolbar';
import { revenueForecastKeys, type RevenueForecastFilters } from '@/features/revenue-forecast/api';
import { useDebouncedSearch } from '@/hooks/use-debounced-search';
import { formatListParam, parseListParam, useListFilters } from '@/hooks/use-list-filters';
import { api } from '@/lib/api';
import { usePermissions } from '@/lib/use-permissions';

/** A BFF-nek küldött query alakja (a szerződés `RevenueForecastQuerySchema`-jának kliens-oldali párja). */
export interface RevenueQuery {
  month: string;
  q: string | undefined;
  status: ForecastStatus[] | undefined;
  billingType: ForecastBillingType | undefined;
  mainCategory: (typeof MAIN_CATEGORIES)[number][] | undefined;
  subCategory: string[] | undefined;
  serviceId: string | undefined;
  planId: string | undefined;
  clientId: string | undefined;
  accountId: string | undefined;
}

export type ForecastStatus = 'active' | 'pending' | 'inactive';
/** A díjtípus-szűrő értékei (a szerződés `BillingTypeSchema`-ja); „mind" = `ALL`. */
export type ForecastBillingType = 'recurring' | 'one_time';
export const BILLING_TYPE_VALUES: ForecastBillingType[] = ['recurring', 'one_time'];
export const STATUS_VALUES: ForecastStatus[] = ['active', 'pending', 'inactive'];
export const MAIN_CATEGORIES = ['product', 'advisory', 'other'] as const;
export const SUB_CATEGORIES = [
  'roomsome',
  'innovation',
  'digital_marketing',
  'revenue_management',
  'web_development',
  'other',
] as const;

/** A státusz-szűrő ALAPJA: a csak-aktív nézet (ugyanez a BFF defaultja üres szűrőre). */
export const DEFAULT_STATUSES: readonly ForecastStatus[] = ['active'];

/**
 * Az ALAPTÓL eltérő-e a státusz-szűrő. Ilyenkor a lap kimondja, mire hat a szűrő és mire NEM (a rögzített
 * viszonyítási kártyák és a tény-számok státusztól függetlenek — `docs/modules/admin/finance.md`).
 */
export function isNonDefaultStatus(statuses: readonly string[]): boolean {
  return !(
    statuses.length === DEFAULT_STATUSES.length &&
    statuses.every((s) => (DEFAULT_STATUSES as readonly string[]).includes(s))
  );
}

/**
 * A szűrők az URL-ben (`rev_` prefix — a `/finance` route `validateSearch`-e ismeri a kulcsokat).
 * A `defaults` egyben a KULCS-KÉSZLET, ezért modul-szintű konstans: stabil referencia, és nem épül újra
 * renderenként. A multi-szűrők vesszős listák (`parseListParam`/`formatListParam`).
 */
export const FILTER_DEFAULTS = {
  rev_q: '',
  rev_status: DEFAULT_STATUSES.join(','),
  rev_billing: ALL,
  rev_main: '',
  rev_sub: '',
  rev_service: ALL,
  rev_plan: ALL,
  rev_client: ALL,
  rev_account: ALL,
};

/** A hook kimenete: a sáv-vezérlők + a lekérdezés kulcsa és query-je. */
export interface RevenueFilters {
  /** A `ListToolbar` sorrend-tartó szűrő-listája. */
  filterItems: ToolbarFilterItem[];
  /** A SZOLGÁLTATÁS-szűrő beállítása (a csomag-szűrő törlésével) — a „Bevétel szolgáltatásonként" kattintása. */
  selectService: (serviceId: string) => void;
  /**
   * A STÁTUSZ-szűrő EGY státuszra (a státusz-sáv kattintása); ha már pontosan arra szűr, vissza az alapra.
   */
  selectStatus: (status: ForecastStatus) => void;
  /** A kereső AZONNALI (lokális) állapota — az URL-be a debounce-olt érték kerül. */
  searchInput: string;
  setSearchInput: (v: string) => void;
  /** A lekérdezés-kulcs anyaga (stabil, primitívekből) — MINDKÉT fül ugyanezzel címzi a cache-t. */
  filters: RevenueForecastFilters;
  /** A BFF-nek küldött query (a `ALL`/üres értékek `undefined`-ra képezve). */
  query: RevenueQuery;
  /** Igaz, ha ügyfél- vagy fiók-szűrő aktív (a legacy egyes blokkjai ettől függenek). */
  scoped: boolean;
  statuses: ForecastStatus[];
  /** A díjtípus-szűrő (`undefined` = mind) — az „eseti" nézet ettől rejti az ismétlődő-only blokkokat. */
  billingType: ForecastBillingType | undefined;
  /**
   * Igaz, ha a felhasználó SZŰKÍTETT (kereső, nem-alap státusz, kategória, szolgáltatás/csomag, ügyfél/fiók).
   * Az ÜRES állapot üzenete ettől függ: „nincs találat a szűrésre" vs. „ebben a hónapban nincs bevétel" —
   * a kettő MÁS teendőt jelent, és a felhasználónak tudnia kell, melyikről van szó.
   */
  hasNarrowing: boolean;
}

export function useRevenueFilters({
  enabled,
  month,
}: {
  enabled: boolean;
  month: string;
}): RevenueFilters {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const canManage = can('service_assignments.manage');

  // SZŰRŐ-ÁLLAPOT AZ URL-BEN (`rev_` prefix — a `/finance` route `validateSearch`-e ismeri a kulcsokat).
  // A hónap a héjban lakik: a három fül UGYANARRA a hónapra néz.
  const { filters: url, setFilters } = useListFilters(FILTER_DEFAULTS);
  // A MULTI-szűrők vesszős listaként utaznak (`parseListParam`); üres = mind. A `useMemo` azért kell, mert a
  // tömb a lekérdezés-kulcs része — új referencia minden renderben újrakérést jelentene.
  const statuses = useMemo(
    () => parseListParam(url.rev_status) as ForecastStatus[],
    [url.rev_status],
  );
  // ismeretlen URL-érték (kézzel átírt link) → mind; a BFF úgyis 400-at adna rá
  const billingType = (BILLING_TYPE_VALUES as string[]).includes(url.rev_billing)
    ? (url.rev_billing as ForecastBillingType)
    : undefined;
  const mainCategory = useMemo(() => parseListParam(url.rev_main), [url.rev_main]);
  const subCategory = useMemo(() => parseListParam(url.rev_sub), [url.rev_sub]);
  const serviceId = url.rev_service;
  const planId = url.rev_plan;
  const clientId = url.rev_client;
  const accountId = url.rev_account;
  const search = url.rev_q;

  // A kereső a lekérdezés KULCSA → debounce kötelező: EGY lekérés **30 Odin-olvasás**, tehát gépelés közben
  // debounce nélkül a lap belefutna a busz percenkénti kérés-plafonjába (és a fojtás élesben kétszer
  // jog-hibának látszott).
  const commitSearch = useCallback((v: string) => setFilters({ rev_q: v }), [setFilters]);
  const { input: searchInput, setInput: setSearchInput } = useDebouncedSearch(search, commitSearch);

  const showRefsFilters = enabled && canManage;

  // Szűrő-opciók forrása (szolgáltatás/csomag/ügyfél/fiók) — manage-jog mögött (a refs endpoint úgy védett).
  const refsQ = api.serviceAssignmentRefs.useQuery(
    revenueForecastKeys.refs(),
    {},
    { enabled: showRefsFilters, queryKey: revenueForecastKeys.refs() },
  );
  const refs = refsQ.data?.status === 200 ? refsQ.data.body : undefined;

  // Kaszkád-reset (a legacy 1:1): fő→(al+szolg+csomag) · al→(szolg+csomag) · szolg→csomag · ügyfél→fiók.
  // MINDEGYIK EGYETLEN `setFilters` hívás: ami atomi szándék, az egy navigáció legyen — több hívás egy
  // kattintásra fölöslegesen ír history-t, és a kaszkád fele elveszne, ha a hívások nem épülnének egymásra.
  const onMainCategory = (v: string[]): void =>
    setFilters({ rev_main: formatListParam(v), rev_sub: '', rev_service: ALL, rev_plan: ALL });
  const onSubCategory = (v: string[]): void =>
    setFilters({ rev_sub: formatListParam(v), rev_service: ALL, rev_plan: ALL });
  const onService = (v: string): void => setFilters({ rev_service: v, rev_plan: ALL });
  const onClient = (v: string): void => setFilters({ rev_client: v, rev_account: ALL });
  // az ÜRES kiválasztás az URL-ben az alapra (csak aktív) esne vissza, és a BFF is azt érti alatta — a „mind"
  // (pl. a „Szűrők törlése") ezért a teljes státusz-halmaz (Andi #95)
  const onStatuses = (v: string[]): void =>
    setFilters({ rev_status: formatListParam(v.length === 0 ? STATUS_VALUES : v) });
  const selectStatus = (s: ForecastStatus): void =>
    onStatuses(statuses.length === 1 && statuses[0] === s ? [...DEFAULT_STATUSES] : [s]);
  const onBillingType = (v: string): void => setFilters({ rev_billing: v });
  const onPlan = (v: string): void => setFilters({ rev_plan: v });
  const onAccount = (v: string): void => setFilters({ rev_account: v });

  // Szolgáltatás/csomag/al-kategória opciók a refs-plan-ekből, a kiválasztott fő-/alkategória szerint szűrve.
  const plansMatching = useMemo(
    () =>
      (refs?.plans ?? []).filter(
        (p) =>
          (mainCategory.length === 0 ||
            (!!p.mainCategory && mainCategory.includes(p.mainCategory))) &&
          (subCategory.length === 0 || (!!p.subCategory && subCategory.includes(p.subCategory))),
      ),
    [refs, mainCategory, subCategory],
  );

  const subCategoryOptions = useMemo(() => {
    // refs-ből származtatott (fő-kat szerint szűrt) distinct alkategóriák; refs nélkül a teljes enum.
    if (refs) {
      const set = new Set(
        refs.plans
          .filter(
            (p) =>
              mainCategory.length === 0 ||
              (!!p.mainCategory && mainCategory.includes(p.mainCategory)),
          )
          .map((p) => p.subCategory)
          .filter((s): s is string => !!s),
      );
      return [...set].map((v) => ({
        value: v,
        label: t(`revenueForecast.subCategory.${v}`, { defaultValue: v }),
      }));
    }
    return SUB_CATEGORIES.map((v) => ({ value: v, label: t(`revenueForecast.subCategory.${v}`) }));
  }, [refs, mainCategory, t]);

  const serviceOptions = useMemo(() => {
    const ids = new Set(plansMatching.map((p) => p.serviceId));
    return (refs?.services ?? [])
      .filter((s) => ids.has(s.id))
      .map((s) => ({ value: s.id, label: s.name }));
  }, [refs, plansMatching]);

  const planOptions = useMemo(() => {
    if (serviceId === ALL) return [];
    return plansMatching
      .filter((p) => p.serviceId === serviceId)
      .map((p) => ({ value: p.id, label: p.name }));
  }, [plansMatching, serviceId]);

  const clientOptions = (refs?.clients ?? []).map((c) => ({ value: c.id, label: c.name }));
  const accountOptions = (refs?.accounts ?? [])
    .filter((a) => clientId === ALL || a.clientId === clientId)
    .map((a) => ({ value: a.id, label: a.name }));

  // A SZŰRŐ-PANEL egyetlen, sorrend-tartó listából renderelődik a KÖZÖS `ListToolbar`-ban — ugyanaz az
  // eszköztár, mint a repo többi lista-felületén (korábban ennek a fülnek saját `ForecastToolbar`-ja volt,
  // bájtra ugyanazzal a popover-, badge- és „Szűrők törlése"-markuppal). A három multi-facet (státusz +
  // fő-/alkategória) a legacy checkbox-dropdownok 1:1 megfelelője; a négy refs-alapú Select csak
  // manage-joggal jelenik meg. Az „aktív szűrők" számát és a törlést innentől az eszköztár számolja.
  const filterItems: ToolbarFilterItem[] = [
    {
      kind: 'multi',
      key: 'status',
      label: t('revenueForecast.filterStatus'),
      values: statuses,
      onChange: onStatuses,
      options: STATUS_VALUES.map((v) => ({ value: v, label: t(`revenueForecast.status.${v}`) })),
    },
    // DÍJTÍPUS a státusz ALATT (user-döntés): mind / ismétlődő / eseti — egyértékű, mint a szerződés
    {
      kind: 'select',
      key: 'billingType',
      label: t('revenueForecast.filterBillingType'),
      value: billingType ?? ALL,
      onChange: onBillingType,
      options: BILLING_TYPE_VALUES.map((v) => ({
        value: v,
        label: t(`revenueForecast.billingType.${v}`),
      })),
    },
    {
      kind: 'multi',
      key: 'mainCategory',
      label: t('revenueForecast.filterMainCategory'),
      values: mainCategory,
      onChange: onMainCategory,
      options: MAIN_CATEGORIES.map((v) => ({
        value: v,
        label: t(`revenueForecast.mainCategory.${v}`),
      })),
    },
    {
      kind: 'multi',
      key: 'subCategory',
      label: t('revenueForecast.filterSubCategory'),
      values: subCategory,
      onChange: onSubCategory,
      options: subCategoryOptions,
    },
    ...(showRefsFilters
      ? ([
          {
            kind: 'select',
            key: 'service',
            // TULAJDONNÉV-lista (adatból) → nem kisbetűsítjük; hosszú, ezért KERESHETŐ (szövegközi keresés + ×).
            preserveCase: true,
            searchable: true,
            label: t('revenueForecast.filterService'),
            value: serviceId,
            onChange: onService,
            options: serviceOptions,
          },
          {
            kind: 'select',
            key: 'plan',
            preserveCase: true,
            searchable: true,
            label: t('revenueForecast.filterPlan'),
            value: planId,
            onChange: onPlan,
            options: planOptions,
            disabled: serviceId === ALL,
          },
          {
            kind: 'select',
            key: 'client',
            preserveCase: true,
            searchable: true,
            label: t('revenueForecast.filterClient'),
            value: clientId,
            onChange: onClient,
            options: clientOptions,
          },
          {
            kind: 'select',
            key: 'account',
            preserveCase: true,
            searchable: true,
            label: t('revenueForecast.filterAccount'),
            value: accountId,
            onChange: onAccount,
            options: accountOptions,
            disabled: clientId === ALL,
          },
        ] satisfies ToolbarFilterItem[])
      : []),
  ];

  // A lekérdezés szűrői (query-kulcs + request).
  const filters = {
    month,
    search,
    statuses,
    billingType: billingType ?? ALL,
    mainCategory,
    subCategory,
    serviceId,
    planId,
    clientId,
    accountId,
  };
  const query = {
    month,
    q: search === '' ? undefined : search,
    status: statuses.length > 0 ? statuses : undefined,
    billingType,
    mainCategory:
      mainCategory.length > 0 ? (mainCategory as (typeof MAIN_CATEGORIES)[number][]) : undefined,
    subCategory: subCategory.length > 0 ? subCategory : undefined,
    serviceId: serviceId === ALL ? undefined : serviceId,
    planId: planId === ALL ? undefined : planId,
    clientId: clientId === ALL ? undefined : clientId,
    accountId: accountId === ALL ? undefined : accountId,
  };

  const hasNarrowing =
    search !== '' ||
    // a státusz akkor NEM szűkít, ha mindhárom ki van választva — az alap (csak aktív) is szűkítés (Andi #95)
    !STATUS_VALUES.every((v) => statuses.includes(v)) ||
    billingType !== undefined ||
    mainCategory.length > 0 ||
    subCategory.length > 0 ||
    [serviceId, planId, clientId, accountId].some((v) => v !== ALL);

  return {
    filterItems,
    searchInput,
    setSearchInput,
    filters,
    query,
    scoped: clientId !== ALL || accountId !== ALL,
    statuses,
    billingType,
    hasNarrowing,
    selectService: onService,
    selectStatus,
  };
}
